// Client layer (TS-2.5.03). Add, edit, delete and reorder against a project's milestone series.
// D-229 originally found edit impossible: ProjectDetail.milestones was MilestoneView[] (the
// RESOLVED display shape - a formatted dateLabel/completionLabel, no rawDate/rawCompletion/
// rawMilestoneTeam), while MilestoneSaveRequest.SaveMilestoneAsync upserts the WHOLE submitted
// MilestoneRecord (PortfolioDataManager.Writes.cs: `UpsertMilestoneAsync(submitted, ...)`, a
// replace, not a merge). Seeding an edit form from the View's formatted text and submitting it
// back would have silently nulled RawMilestoneTeam on every edit. That gap is now closed:
// ProjectDetail also carries `milestoneRecords: MilestoneRecord[]` - the raw records, in the same
// shape MilestoneSaveRequest.milestone takes back - alongside the views. Every edit draft below is
// seeded from the matching RECORD (matched by key), never from the view, so rawMilestoneTeam,
// sortOrder, businessProjectId and projectKey all round-trip untouched; if no record matches a
// view's key, that row offers no Edit affordance at all.
// The "Is go-live" checkbox on both the add and edit forms matters beyond cosmetics: it is what
// lets GoLiveResolutionEngine's step 1 (the explicit isGoLiveFlag) resolve a user-created
// milestone as the project's go-live milestone at all - the add form used to hardcode false, so
// nothing a user created could ever be picked up that way.
// The milestone team (rawMilestoneTeam, imported from the workbook's "Milestone Team" column) is
// shown and edited here, read from the matching RECORD since MilestoneView carries no team member
// at all - the roadmap timeline and deck views deliberately do not show it yet (owner ruling
// 2026-09-30: project page only).
// Completion and Team are the two of this form's fields the owner ruled CLOSED (2026-10-03): each
// renders as a `VocabularySelect` rather than the old `<input list>` + `<datalist>` pair - see
// that component's header for why, and for the "(not in list)" handling of a stored value outside
// the active vocabulary. "Save and add another" (D-294) still carries the team FORWARD into the
// next add-draft; with a select that is simply the same string value pre-selected.
import { useRef, useState, type RefObject } from "react";
import { DateField } from "../../components/DateField";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { VocabularySelect } from "../../components/VocabularySelect";
import type { MilestoneRecord, MilestoneView, ReferenceValue } from "../../api/generated/contracts";
import { portfolioDataApi } from "../../api/portfolioData";
import { strings } from "../../strings";

export interface MilestonesSectionProps {
  readonly projectKey: string;
  readonly milestones: readonly MilestoneView[];
  readonly milestoneRecords: readonly MilestoneRecord[];
  readonly completionOptions: readonly ReferenceValue[];
  readonly teamOptions: readonly ReferenceValue[];
  readonly placeholderOptions: ReadonlyArray<{ token: string; label: string }>;
  readonly readOnly: boolean;
  readonly onChanged: () => void;
}

const S = strings.projects.milestones;

interface MilestoneDraft {
  readonly description: string;
  readonly date: string;
  readonly completion: string;
  readonly isGoLive: boolean;
  readonly team: string;
}

const EMPTY_DRAFT: MilestoneDraft = { description: "", date: "", completion: "", isGoLive: false, team: "" };

function draftFromRecord(record: MilestoneRecord): MilestoneDraft {
  return {
    description: record.description ?? "",
    date: record.rawDate === null || record.rawDate === undefined ? "" : String(record.rawDate),
    completion: record.rawCompletion ?? "",
    isGoLive: record.isGoLiveFlag ?? false,
    team: record.rawMilestoneTeam ?? "",
  };
}

interface MilestoneFieldsProps {
  readonly idPrefix: string;
  readonly draft: MilestoneDraft;
  readonly onChange: (patch: Partial<MilestoneDraft>) => void;
  readonly completionOptions: readonly ReferenceValue[];
  readonly teamOptions: readonly ReferenceValue[];
  readonly placeholderOptions: ReadonlyArray<{ token: string; label: string }>;
  // Only the add form needs this: "Save and add another" returns focus here so the owner can
  // keep entering consecutive milestones without reaching for the mouse.
  readonly descriptionInputRef?: RefObject<HTMLInputElement>;
}

function MilestoneFields({
  idPrefix,
  draft,
  onChange,
  completionOptions,
  teamOptions,
  placeholderOptions,
  descriptionInputRef,
}: MilestoneFieldsProps) {
  return (
    <>
      <FormField id={`${idPrefix}-description`} label={S.fields.description} required>
        <input
          type="text"
          required
          ref={descriptionInputRef}
          value={draft.description}
          onChange={(event) => onChange({ description: event.target.value })}
          {...formFieldControlProps(`${idPrefix}-description`)}
        />
      </FormField>
      <FormField id={`${idPrefix}-date`} label={S.fields.date}>
        <DateField
          id={`${idPrefix}-date`}
          label={S.fields.date}
          value={draft.date}
          onChange={(value) => onChange({ date: value })}
          placeholderOptions={placeholderOptions}
        />
      </FormField>
      <FormField id={`${idPrefix}-completion`} label={S.fields.completion}>
        <VocabularySelect
          id={`${idPrefix}-completion`}
          value={draft.completion}
          onChange={(next) => onChange({ completion: next })}
          options={completionOptions}
        />
      </FormField>
      <FormField id={`${idPrefix}-team`} label={S.fields.team}>
        <VocabularySelect
          id={`${idPrefix}-team`}
          value={draft.team}
          onChange={(next) => onChange({ team: next })}
          options={teamOptions}
        />
      </FormField>
      <FormField id={`${idPrefix}-is-go-live`} label={S.fields.isGoLive}>
        <input
          type="checkbox"
          checked={draft.isGoLive}
          onChange={(event) => onChange({ isGoLive: event.target.checked })}
          {...formFieldControlProps(`${idPrefix}-is-go-live`)}
        />
      </FormField>
    </>
  );
}

export function MilestonesSection({
  projectKey,
  milestones,
  milestoneRecords,
  completionOptions,
  teamOptions,
  placeholderOptions,
  readOnly,
  onChanged,
}: MilestonesSectionProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [addDraft, setAddDraft] = useState<MilestoneDraft>(EMPTY_DRAFT);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<MilestoneDraft>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const descriptionInputRef = useRef<HTMLInputElement>(null);

  // "Save and add another" keeps the form open across several quick saves, but `milestones` (and
  // therefore `ordered.length`) only reflects the newest save once the parent's onChanged()
  // refetch resolves - which is async and may not have landed before the next save fires. Without
  // this counter, two rapid "add another" clicks would both compute sortOrder from the same stale
  // ordered.length and collide. We count adds made since the props last changed and add that to
  // ordered.length; the count resets to 0 the moment a new `milestones` reference arrives (i.e.
  // the refetch caught up).
  const addsSincePropsChangeRef = useRef(0);
  const previousMilestonesRef = useRef(milestones);
  if (previousMilestonesRef.current !== milestones) {
    previousMilestonesRef.current = milestones;
    addsSincePropsChangeRef.current = 0;
  }

  const ordered = [...milestones].sort((left, right) => left.sortOrder - right.sortOrder);

  function recordFor(milestoneKey: string): MilestoneRecord | undefined {
    return milestoneRecords.find((record) => record.key === milestoneKey);
  }

  async function performAdd(keepOpen: boolean) {
    setBusy(true);
    const sortOrder = ordered.length + addsSincePropsChangeRef.current;
    try {
      await portfolioDataApi.createMilestone({
        milestone: {
          key: null,
          businessProjectId: null,
          projectKey,
          description: addDraft.description,
          rawDate: addDraft.date.length > 0 ? addDraft.date : null,
          rawCompletion: addDraft.completion.length > 0 ? addDraft.completion : null,
          isGoLiveFlag: addDraft.isGoLive,
          sortOrder,
          rawMilestoneTeam: addDraft.team.length > 0 ? addDraft.team : null,
          concurrencyToken: null,
        },
        concurrencyToken: null,
      });
      addsSincePropsChangeRef.current += 1;
      if (keepOpen) {
        // Consecutive milestones entered in one sitting usually share a team, so keep it
        // pre-filled (the user can still clear it) - but every other field describes the
        // just-saved milestone and must not leak into the next one. Focus goes back to
        // Description so the owner can keep typing without touching the mouse.
        setAddDraft({ ...EMPTY_DRAFT, team: addDraft.team });
        descriptionInputRef.current?.focus();
      } else {
        setAddDraft(EMPTY_DRAFT);
        setShowAdd(false);
      }
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function addMilestone() {
    await performAdd(false);
  }

  async function addAnotherMilestone() {
    await performAdd(true);
  }

  async function saveEdit(record: MilestoneRecord) {
    if (!record.key) return;
    setBusy(true);
    try {
      await portfolioDataApi.updateMilestone(record.key, {
        milestone: {
          ...record,
          description: editDraft.description,
          rawDate: editDraft.date.length > 0 ? editDraft.date : null,
          rawCompletion: editDraft.completion.length > 0 ? editDraft.completion : null,
          isGoLiveFlag: editDraft.isGoLive,
          rawMilestoneTeam: editDraft.team.length > 0 ? editDraft.team : null,
        },
        concurrencyToken: record.concurrencyToken,
      });
      setEditingKey(null);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function deleteMilestone(milestoneKey: string) {
    setBusy(true);
    try {
      await portfolioDataApi.deleteMilestone(milestoneKey);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= ordered.length) return;

    const reordered = [...ordered];
    const a = reordered[index];
    const b = reordered[target];
    if (!a || !b) return;
    reordered[index] = b;
    reordered[target] = a;

    setBusy(true);
    try {
      await portfolioDataApi.reorderMilestones(
        projectKey,
        reordered.map((milestone) => milestone.key),
      );
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="eg-milestones eg-project-detail__card" aria-labelledby="eg-milestones-heading">
      <h2 id="eg-milestones-heading">{S.heading}</h2>

      {ordered.length === 0 ? (
        <p>{S.empty}</p>
      ) : (
        <ol className="eg-milestones__list">
          {ordered.map((milestone, index) => {
            const record = recordFor(milestone.key);
            const isEditing = editingKey === milestone.key && record;

            return (
              <li key={milestone.key} className="eg-milestones__item">
                {isEditing && record ? (
                  <form
                    className="eg-milestones__edit-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void saveEdit(record);
                    }}
                  >
                    <MilestoneFields
                      idPrefix={`eg-milestone-edit-${milestone.key}`}
                      draft={editDraft}
                      onChange={(patch) => setEditDraft((current) => ({ ...current, ...patch }))}
                      completionOptions={completionOptions}
                      teamOptions={teamOptions}
                      placeholderOptions={placeholderOptions}
                    />
                    <div className="eg-project-detail__actions">
                      <button type="submit" className="eg-project-detail__btn--primary" disabled={busy}>
                        {strings.projects.detail.save}
                      </button>
                      <button type="button" disabled={busy} onClick={() => setEditingKey(null)}>
                        {strings.projects.detail.cancel}
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <span
                      className="eg-milestones__completion-pill"
                      style={{ backgroundColor: milestone.completionColorHex }}
                    >
                      {milestone.completionLabel}
                    </span>
                    <span className="eg-milestones__label">
                      {milestone.label}
                      {milestone.isGoLive && <span className="eg-milestones__golive-badge"> (Go-live)</span>}
                    </span>
                    {record?.rawMilestoneTeam && record.rawMilestoneTeam.length > 0 && (
                      <span className="eg-milestones__team">{record.rawMilestoneTeam}</span>
                    )}
                    <span className="eg-milestones__date">{milestone.dateLabel}</span>
                    {!readOnly && (
                      <span className="eg-milestones__actions">
                        {record && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              setEditDraft(draftFromRecord(record));
                              setEditingKey(milestone.key);
                            }}
                          >
                            {S.edit}
                          </button>
                        )}
                        <button type="button" disabled={busy || index === 0} onClick={() => void move(index, -1)}>
                          {S.moveUp}
                        </button>
                        <button
                          type="button"
                          disabled={busy || index === ordered.length - 1}
                          onClick={() => void move(index, 1)}
                        >
                          {S.moveDown}
                        </button>
                        <button
                          type="button"
                          className="eg-project-detail__btn--danger"
                          disabled={busy}
                          onClick={() => void deleteMilestone(milestone.key)}
                        >
                          {S.delete}
                        </button>
                      </span>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {!readOnly && (
        <>
          {!showAdd ? (
            <button type="button" onClick={() => setShowAdd(true)}>
              {S.add}
            </button>
          ) : (
            <form
              className="eg-milestones__add-form"
              onSubmit={(event) => {
                event.preventDefault();
                void addMilestone();
              }}
            >
              <MilestoneFields
                idPrefix="eg-milestone-add"
                draft={addDraft}
                onChange={(patch) => setAddDraft((current) => ({ ...current, ...patch }))}
                completionOptions={completionOptions}
                teamOptions={teamOptions}
                placeholderOptions={placeholderOptions}
                descriptionInputRef={descriptionInputRef}
              />
              <div className="eg-project-detail__actions">
                {/* Plain submit (type="submit") stays the default so Enter in a text field saves
                    and closes the form, as before. "Save and add another" is type="button" with
                    its own handler so it never becomes the implicit Enter target. */}
                <button type="submit" className="eg-project-detail__btn--primary" disabled={busy}>
                  {S.add}
                </button>
                <button type="button" disabled={busy} onClick={() => void addAnotherMilestone()}>
                  {S.addAnother}
                </button>
                <button type="button" disabled={busy} onClick={() => setShowAdd(false)}>
                  {strings.projects.detail.cancel}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}
